import { useState } from "react";
import { trpc } from "../lib/trpc.js";
import {
  Server, Activity, CheckCircle2, XCircle, AlertTriangle, RefreshCw,
  Clock, Cpu, HardDrive, Wifi, Globe, Zap, BarChart3, ArrowUpRight,
  ArrowDownRight, Loader2, Shield,
} from "lucide-react";

type SystemTab = "health" | "metrics";

// ─── Health Tab ───────────────────────────────────────────────────────────────

function HealthTab() {
  const { data: health, isLoading, refetch } = trpc.system.health.useQuery(undefined, {
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="animate-pulse bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-muted rounded-lg" />
              <div className="flex-1">
                <div className="h-4 bg-muted rounded w-1/3 mb-2" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const services = health?.services || [];
  const healthyCount = services.filter((s: any) => s.status === "healthy").length;
  const totalCount = services.length;

  const overallStatus = healthyCount === totalCount ? "healthy" : healthyCount > 0 ? "degraded" : "down";

  return (
    <div className="space-y-4">
      {/* Overall Status */}
      <div className={`bg-card border rounded-xl p-5 ${
        overallStatus === "healthy" ? "border-success/30" :
        overallStatus === "degraded" ? "border-yellow-500/30" :
        "border-destructive/30"
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              overallStatus === "healthy" ? "bg-success/10" :
              overallStatus === "degraded" ? "bg-yellow-500/10" :
              "bg-destructive/10"
            }`}>
              {overallStatus === "healthy" ? (
                <CheckCircle2 className="w-6 h-6 text-success" />
              ) : overallStatus === "degraded" ? (
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
              ) : (
                <XCircle className="w-6 h-6 text-destructive" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">
                {overallStatus === "healthy" ? "Todos os Sistemas Operacionais" :
                 overallStatus === "degraded" ? "Sistemas Parcialmente Degradados" :
                 "Sistemas Fora do Ar"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {healthyCount}/{totalCount} serviços saudáveis
              </p>
            </div>
          </div>
          <button onClick={() => refetch()} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-2 border border-border rounded-lg hover:bg-secondary">
            <RefreshCw className="w-3.5 h-3.5" /> Verificar
          </button>
        </div>
      </div>

      {/* Service Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {services.map((service: any, i: number) => {
          const isHealthy = service.status === "healthy";
          const isDegraded = service.status === "degraded";

          return (
            <div key={i} className={`bg-card border rounded-xl p-4 transition-colors ${
              isHealthy ? "border-border hover:border-success/30" :
              isDegraded ? "border-yellow-500/20" :
              "border-destructive/20"
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isHealthy ? "bg-success/10" : isDegraded ? "bg-yellow-500/10" : "bg-destructive/10"
                  }`}>
                    {service.name?.includes("Brain") ? <Cpu className={`w-5 h-5 ${isHealthy ? "text-success" : isDegraded ? "text-yellow-500" : "text-destructive"}`} /> :
                     service.name?.includes("LLM") ? <Zap className={`w-5 h-5 ${isHealthy ? "text-success" : isDegraded ? "text-yellow-500" : "text-destructive"}`} /> :
                     service.name?.includes("Media") ? <HardDrive className={`w-5 h-5 ${isHealthy ? "text-success" : isDegraded ? "text-yellow-500" : "text-destructive"}`} /> :
                     service.name?.includes("Platform") ? <Globe className={`w-5 h-5 ${isHealthy ? "text-success" : isDegraded ? "text-yellow-500" : "text-destructive"}`} /> :
                     <Server className={`w-5 h-5 ${isHealthy ? "text-success" : isDegraded ? "text-yellow-500" : "text-destructive"}`} />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{service.name}</p>
                    <p className="text-[10px] text-muted-foreground">{service.url || "Local"}</p>
                  </div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                  isHealthy ? "bg-success/10 text-success border-success/20" :
                  isDegraded ? "bg-yellow-500/10 text-yellow-500 border-yellow-500/20" :
                  "bg-destructive/10 text-destructive border-destructive/20"
                }`}>
                  {isHealthy ? "Saudável" : isDegraded ? "Degradado" : "Offline"}
                </span>
              </div>
              {service.latency !== undefined && (
                <div className="mt-3 flex items-center gap-4 text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {service.latency}ms</span>
                  {service.version && <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> v{service.version}</span>}
                  {service.uptime && <span className="flex items-center gap-1"><Activity className="w-3 h-3" /> {service.uptime}</span>}
                </div>
              )}
              {service.error && (
                <p className="mt-2 text-[10px] text-destructive bg-destructive/5 rounded-lg px-2 py-1">{service.error}</p>
              )}
            </div>
          );
        })}
      </div>

      {services.length === 0 && (
        <div className="text-center py-12 bg-card border border-border rounded-xl">
          <Server className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
          <h3 className="text-base font-semibold text-foreground mb-1">Nenhum serviço configurado</h3>
          <p className="text-xs text-muted-foreground">Configure as URLs dos serviços para monitorar.</p>
        </div>
      )}
    </div>
  );
}

// ─── Metrics Tab ──────────────────────────────────────────────────────────────

function MetricsTab() {
  const { data: metrics, isLoading } = trpc.system.metrics.useQuery(undefined, {
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="animate-pulse bg-card border border-border rounded-xl p-4">
            <div className="h-3 bg-muted rounded w-1/2 mb-3" />
            <div className="h-6 bg-muted rounded w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  const data: any = metrics || {};

  const cards = [
    { label: "Tasks Hoje", value: data.tasksToday || 0, icon: Activity, color: "text-primary", trend: data.tasksTrend },
    { label: "Mensagens Chat", value: data.chatMessages || 0, icon: Wifi, color: "text-blue-400", trend: data.chatTrend },
    { label: "Custo LLM (Hoje)", value: `$${(data.llmCostToday || 0).toFixed(4)}`, icon: Zap, color: "text-yellow-500", trend: data.costTrend },
    { label: "Uptime", value: data.uptime || "N/A", icon: Clock, color: "text-success" },
  ];

  return (
    <div className="space-y-4">
      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((card, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <card.icon className={`w-4 h-4 ${card.color}`} />
              <span className="text-xs text-muted-foreground">{card.label}</span>
            </div>
            <p className="text-xl font-bold text-foreground">{card.value}</p>
            {card.trend !== undefined && (
              <div className={`flex items-center gap-1 mt-1 text-[10px] ${card.trend >= 0 ? "text-success" : "text-destructive"}`}>
                {card.trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(card.trend)}% vs ontem
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-primary" /> Atividade Recente
        </h3>
        {data.recentActivity && data.recentActivity.length > 0 ? (
          <div className="space-y-2">
            {data.recentActivity.map((item: any, i: number) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                <div className={`w-2 h-2 rounded-full ${
                  item.type === "task" ? "bg-primary" :
                  item.type === "chat" ? "bg-blue-400" :
                  item.type === "error" ? "bg-destructive" :
                  "bg-muted-foreground"
                }`} />
                <p className="text-xs text-foreground flex-1">{item.description}</p>
                <span className="text-[10px] text-muted-foreground">
                  {item.timestamp ? new Date(item.timestamp).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : ""}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-6">Nenhuma atividade recente registrada.</p>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function System() {
  const [activeTab, setActiveTab] = useState<SystemTab>("health");

  const tabs: { id: SystemTab; label: string; icon: typeof Server }[] = [
    { id: "health", label: "Health", icon: Activity },
    { id: "metrics", label: "Métricas", icon: BarChart3 },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
          <Server className="w-6 h-6 text-primary" />
          Sistema
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Monitoramento de saúde, métricas e atividade da infraestrutura
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-secondary/50 border border-border rounded-xl w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? "bg-card text-foreground shadow-sm border border-border"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? "text-primary" : ""}`} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "health" && <HealthTab />}
      {activeTab === "metrics" && <MetricsTab />}
    </div>
  );
}
